import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import { HeadsUpSidebar } from "@/components/heads-up";
import { MemItSidePanelView } from "@/components/mem-it";
import styled from "@emotion/styled";
import { motion } from "motion/react";
import { useAppStore } from "@/store";
import { SidePanelRouteType, SidePanelTab } from "@/store/routing/types";
import { SIDE_PANEL_COLLAPSED_HEIGHT_PX, OPEN_TOP_GAP_PX } from "@/app/router/constants";
import { ZIndex } from "@/domains/design/constants";
import { RedirectToActiveOrNewSession } from "@/components/mem-it/RedirectToActiveOrNewSession";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export const SidePanelRouter = observer(function SidePanelRouter() {
  const { pageStore, store } = useAppStore();
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  const memItStore = pageStore.memItPage;
  const sidePanelStore = store.sidePanel;
  const storeSessionId = memItStore.session?.id;
  const activeRoute = sidePanelStore.activeRoute;
  const activeTabId = sidePanelStore.activeTabId;
  const isChatOpen = activeTabId === SidePanelTab.MEM_IT;
  const routeSessionId =
    activeRoute.type === SidePanelRouteType.MEM_IT ? activeRoute.sessionId : undefined;

  useEffect(() => {
    // Prefer the side panel route's sessionId when available
    if (routeSessionId) {
      setSessionId(routeSessionId);
      return;
    }

    // Fall back to the current session in the store if available
    if (storeSessionId) {
      setSessionId(storeSessionId);
      return;
    }

    // Otherwise create or get a placeholder session id
    memItStore
      .getPlaceholderSessionId()
      .then(id => {
        setSessionId(id);
      })
      .catch(error => {
        logger.error({
          message: "Error getting placeholder session id in SidePanelRouter",
          info: {
            error: objectModule.safeErrorAsJson(error as Error),
          },
        });
      });
  }, [memItStore, routeSessionId, storeSessionId]);

  return (
    <Container>
      <HeadsUpContainer>
        <HeadsUpSidebar />
      </HeadsUpContainer>

      <MemItOverlay
        animate={{
          top: isChatOpen ? OPEN_TOP_GAP_PX : `calc(100% - ${SIDE_PANEL_COLLAPSED_HEIGHT_PX}px)`,
        }}
        transition={{ type: "spring", bounce: 1, mass: 0.17, stiffness: 200 }}
      >
        <MemItContainer
          onClick={() => {
            if (!isChatOpen) {
              sidePanelStore.goToMemIt(sessionId);
            }
          }}
        >
          {sessionId ? (
            <MemItSidePanelView
              sessionId={sessionId}
              isClosed={!isChatOpen}
              onClose={sidePanelStore.goToHeadsUp}
            />
          ) : routeSessionId || storeSessionId ? null : (
            <RedirectToActiveOrNewSession />
          )}
        </MemItContainer>
      </MemItOverlay>
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const HeadsUpContainer = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
`;

const MemItOverlay = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${ZIndex.SidebarMemItOverlay};
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const MemItContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
